import { Injectable } from '@angular/core';
import { NavigationEnd, Router } from '@angular/router';
import { addHours, isAfter } from 'date-fns';
import { filter } from 'rxjs/operators';
import { environment } from 'src/environments/environment';
import { AppString } from '../_interfaces';
import { bleachrHTTP } from '../_classes/http';
import { SsrlocalstoreService } from './ssr-localstore.service';

@Injectable({
  providedIn: 'root'
})
export class AppStringsService {
  public cache: Map<string, string> = new Map();
  public supported_locales: Map<string, string> = new Map([
    ['en', 'English'],
    ['fr', 'French'],
    ['es', 'Spanish'],
    ['de', 'German'],
    ['nl', 'Dutch']
  ]);

  private memoryStore: { ttl: string; cache: AppString[] } | null = null;

  private memoryPreferredLanguage: string = 'en';

  constructor(
    private router: Router,
    private http: bleachrHTTP,
    private ssr: SsrlocalstoreService
  ) {}

  async handleInit() {
    await this.build();

    if (this.ssr.isClientSide) {
      this.router.events
        .pipe(filter(e => e instanceof NavigationEnd))
        .subscribe(() => {
          this.checkTTL();
        });
    }
  }

  async build(rebuild: boolean = false) {
    this.setDefaultLang();
    await this.buildLocalStore(rebuild);
    await this.buildCache();
  }

  private setDefaultLang() {
    const preferred = this.ssr.getItem('preferredLanguage');
    if (preferred) return;

    if (this.ssr.isClientSide) {
      this.ssr.setItem('preferredLanguage', 'en');
    } else {
      this.memoryPreferredLanguage = 'en';
    }
  }

  private async checkTTL() {
    if (!this.ssr.isClientSide) return;

    const stored = this.getStoredAppStrings();
    if (!stored) return;

    if (isAfter(new Date(), new Date(stored.ttl))) {
      await this.buildLocalStore(true);
      await this.buildCache();
    }
  }

  private async buildLocalStore(rebuild: boolean = false) {
    if (!this.ssr.isClientSide) {
      return;
    }

    const stored = this.getStoredAppStrings();
    if (!rebuild && stored && !isAfter(new Date(), new Date(stored.ttl))) {
      return;
    }

    return await this.http
      .get<{ data: AppString[]; error: string }>(
        `${environment.ELIXIR_API_FOUR}/strings?locale_override=${this.getpreferredLanguage()}&trim=y`
      )
      .then((res) => {
        const payload = { ttl: addHours(new Date(), 1).toISOString(), cache: res.data };
        this.setStoredAppStrings(payload);
        return res.data;
      })
      .catch((error) => {
        console.error(error);
        return [];
      });
  }

  private async buildCache(): Promise<null> {
    const stored = this.getStoredAppStrings();
    if (!stored) {
      return null;
    }

    this.cache.clear();
    stored.cache.forEach((k) => {
      this.cache.set(k.key, k.string);
    });

    return null;
  }

  private getStoredAppStrings(): { ttl: string; cache: AppString[] } | null {
    if (this.ssr.isClientSide) {
      const raw = this.ssr.getItem('appStrings');
      if (!raw) return null;

      try {
        const parsed = JSON.parse(raw) as { ttl: string; cache: AppString[] };
        if (!parsed?.ttl || !Array.isArray(parsed.cache)) return null;
        return parsed;
      } catch {
        return null;
      }
    }

    return this.memoryStore;
  }

  private setStoredAppStrings(payload: { ttl: string; cache: AppString[] }) {
    if (this.ssr.isClientSide) {
      this.ssr.setItem('appStrings', JSON.stringify(payload));
    } else {
      this.memoryStore = payload;
    }
  }

  getpreferredLanguage(): string {
    const stored = this.ssr.getItem('preferredLanguage');
    return stored ? stored : this.memoryPreferredLanguage || 'en';
  }

  getDisplayName(locale?: string) {
    if (!locale) {
      const locale_store = this.getpreferredLanguage();
      return this.supported_locales.has(locale_store) ? this.supported_locales.get(locale_store) : '';
    }

    return this.supported_locales.has(locale) ? this.supported_locales.get(locale) : '';
  }

  getAppString(key_name: string): string {
    const normalizedKey = key_name?.trim?.();
    const val = this.cache.get(normalizedKey);
    return (typeof val === 'string' && val) ? val : normalizedKey;
  }

  getReplacedValue(key: string, replacements: Array<string>) {
    const base = this.getAppString(key);
    let formatted_string = base;

    replacements.forEach(replacement => {
      formatted_string = formatted_string.replace('%@', replacement);
    });

    return formatted_string;
  }

  async setpreferredLanguage(locale: string) {
    if (this.ssr.isClientSide) {
      this.ssr.setItem('preferredLanguage', locale);
      await this.buildLocalStore(true);
      await this.buildCache();
    } else {
      this.memoryPreferredLanguage = locale || 'en';
    }
    return;
  }
}
